import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabaseClient';
import NavDrawer from './NavDrawer';
import ProvenanceSection from './components/ProvenanceSection';
import { imageUrl } from './CardImage';
import { SET_REGISTRY } from './setRegistry';

// Product an insert set was released in, e.g. "Chrome Galaxy" — the row
// already shows the year, so the registry shortName's year is dropped
// (shortNames carry it leading or trailing depending on the set).
function parentSetLabel(slug) {
  const entry = slug && SET_REGISTRY.find(r => r.id === slug);
  return entry ? entry.shortName.replace(/\s+\d{4}$/, '').replace(/^\d{4}\s+/, '') : null;
}

// --- Color tokens (mirror brand guide) ---
const C = {
  gold: 'var(--gold)',
  cyan: 'var(--cyan)',
  green: 'var(--green)',
  purple: 'var(--purple)',
  red: 'var(--red-2)',
  bg: 'var(--bg)',
  card: 'var(--panel)',
  raised: 'var(--panel-3)',
  line: 'rgba(var(--cyan-rgb),0.14)',
  lineSoft: 'rgba(var(--wash-rgb),0.07)',
  ink: 'var(--text)',
  inkDim: 'var(--muted-5)',
  inkFaint: '#5c6a80',
};

const DISPLAY = "'Orbitron', monospace";
const BODY = "'Rajdhani', sans-serif";
const MONO = "'Share Tech Mono', monospace";

const CONTACT = 'keeper18@collectionkeeper.org';

// Inline styles can't do @media, so responsive rules live in a scoped sheet.
const RESPONSIVE_CSS = `
.ap-page {
  min-height: 100vh;
  background: ${C.bg};
  font-family: ${BODY};
  font-size: 17px;
  line-height: 1.55;
  color: ${C.ink};
  -webkit-font-smoothing: antialiased;
  position: relative;
}
.ap-page::before {
  content: "";
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background:
    radial-gradient(900px 500px at 12% -8%, rgba(var(--cyan-rgb),.07), transparent 60%),
    radial-gradient(700px 420px at 92% 4%, rgba(var(--gold-rgb),.05), transparent 60%);
}
.ap-wrap { position: relative; z-index: 1; max-width: 1180px; margin: 0 auto; padding: 0 clamp(16px, 4vw, 40px); }
.ap-clear-nav { padding-right: 56px; }
.ap-hero-grid { display: grid; grid-template-columns: minmax(0,1fr) 230px; gap: clamp(24px, 5vw, 56px); align-items: start; }
.ap-portrait { position: sticky; top: 76px; }
.ap-stats { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 1px; margin-top: 30px; background: ${C.lineSoft}; border: 1px solid ${C.lineSoft}; }
.ap-setgrid { display: grid; grid-template-columns: repeat(auto-fill, minmax(258px, 1fr)); gap: 10px; margin-top: 20px; }
.ap-setrow:hover { border-color: ${C.line} !important; background: ${C.raised} !important; transform: translateX(2px); }
.ap-traits { display: grid; grid-template-columns: repeat(auto-fit, minmax(168px, 1fr)); gap: 10px; margin-top: 22px; }
.ap-ladder { display: grid; grid-template-columns: repeat(auto-fit, minmax(186px, 1fr)); gap: 10px; margin: 20px 0 6px; }
.ap-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(196px, 1fr)); gap: 16px; margin-top: 22px; }
.ap-sample:hover { border-color: ${C.gold} !important; transform: translateY(-3px); }
.ap-sheets { display: grid; grid-template-columns: repeat(auto-fill, minmax(196px, 1fr)); gap: 14px; margin-top: 18px; }
.ap-owned { display: grid; grid-template-columns: repeat(auto-fill, minmax(184px, 1fr)); gap: 14px; margin-top: 20px; }
.ap-connect { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 10px; margin-top: 20px; max-width: 940px; }
.ap-stylesamples { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 14px; margin-top: 18px; max-width: 940px; }
.ap-stylesample { display: block; background: ${C.card}; border: 1px solid ${C.lineSoft}; border-radius: 3px; overflow: hidden; transition: .18s; text-decoration: none; }
.ap-stylesample:hover { border-color: ${C.purple}; transform: translateY(-3px); }
.ap-stylesample.wide { grid-column: span 2; }
.ap-fchips { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 24px; }
.ap-fchip {
  font-family: ${MONO}; font-size: 10.5px; letter-spacing: .12em; text-transform: uppercase;
  padding: 7px 12px; border-radius: 2px; border: 1px solid ${C.lineSoft}; background: transparent;
  color: ${C.inkDim}; cursor: pointer; transition: .15s;
}
.ap-fchip:hover { color: ${C.ink}; border-color: ${C.line}; }
.ap-fchip.on { background: ${C.cyan}; border-color: ${C.cyan}; color: ${C.bg}; }
.ap-connectcard { display: block; background: ${C.card}; border: 1px solid ${C.lineSoft}; border-top: 2px solid ${C.cyan}; border-radius: 2px; padding: 14px; text-decoration: none; transition: .15s; }
.ap-connectcard:hover { border-color: ${C.cyan}; background: ${C.raised}; transform: translateY(-2px); }
.ap-owncard { background: ${C.card}; border: 1px solid ${C.lineSoft}; border-radius: 3px; overflow: hidden; transition: .18s; }
.ap-owncard:hover { border-color: ${C.green}; transform: translateY(-3px); }
.ap-sheetcard { background: ${C.card}; border: 1px solid ${C.lineSoft}; border-radius: 3px; overflow: hidden; transition: .18s; display: block; text-decoration: none; }
.ap-sheetcard:hover { border-color: ${C.gold}; transform: translateY(-3px); }
.ap-gap { margin-top: 20px; border: 1px dashed rgba(var(--red-2-rgb),.5); border-radius: 3px; background: linear-gradient(180deg, rgba(var(--red-2-rgb),.07), transparent); padding: 24px; display: grid; grid-template-columns: 150px minmax(0,1fr); gap: 24px; align-items: center; }
.ap-sigimgs { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 14px; margin-top: 20px; max-width: 1240px; }
.ap-sigimg { display: block; background: ${C.card}; border: 1px solid ${C.line}; border-radius: 3px; overflow: hidden; transition: .18s; }
.ap-sigimg:hover { border-color: ${C.gold}; transform: translateY(-3px); }
.ap-btn {
  font-family: ${DISPLAY}; font-weight: 700; font-size: 12px; letter-spacing: .1em; text-transform: uppercase;
  padding: 12px 20px; border-radius: 2px; border: 1px solid ${C.cyan}; color: ${C.cyan};
  background: transparent; cursor: pointer; transition: .16s; text-decoration: none; display: inline-block;
}
.ap-btn:hover { background: ${C.cyan}; color: ${C.bg}; text-decoration: none; }
.ap-btn.solid { background: ${C.gold}; border-color: ${C.gold}; color: var(--chip-gold); }
.ap-btn.solid:hover { filter: brightness(1.12); }
.ap-btn.ghost { border-color: ${C.line}; color: ${C.inkDim}; }
.ap-btn.ghost:hover { background: rgba(var(--wash-rgb),.06); color: ${C.ink}; }
.ap-tl-item:hover .ap-tl-bar { background: linear-gradient(180deg, ${C.gold}, rgba(var(--gold-rgb),.25)) !important; }
@media (max-width: 820px) {
  .ap-hero-grid { grid-template-columns: 1fr; }
  .ap-portrait { max-width: 190px; position: static; }
  .ap-stats { grid-template-columns: repeat(2, 1fr); }
  .ap-gap { grid-template-columns: 1fr; }
  .ap-gap .ap-gapframe { max-width: 150px; }
}
@media (max-width: 440px) {
  .ap-stylesample.wide { grid-column: auto; }
}
`;

// --- Small helpers ---

function paragraphs(md) {
  if (!md) return [];
  return md.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
}

// signature_md is stored as INTRO:/KNOWN:/NEED: prefixed lines.
function parseSignature(md) {
  const knowns = [];
  let need = '';
  let intro = '';
  (md || '').split('\n').forEach(line => {
    if (line.startsWith('INTRO:')) intro = line.slice(6).trim();
    else if (line.startsWith('KNOWN:')) knowns.push(line.slice(6).trim());
    else if (line.startsWith('NEED:')) need = line.slice(5).trim();
  });
  return { intro, knowns, need };
}

function CardFrame({ children, dashed, tag }) {
  return (
    <div style={{
      aspectRatio: '2.5 / 3.5', background: C.card,
      border: `1px ${dashed ? 'dashed rgba(var(--red-2-rgb),.4)' : `solid ${C.line}`}`,
      borderRadius: 3, overflow: 'hidden', position: 'relative',
      display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 16,
    }} className={dashed ? 'ap-gapframe' : undefined}>
      {tag && (
        <span style={{
          position: 'absolute', top: 9, right: 9, fontFamily: MONO, fontSize: 9.5, letterSpacing: '.1em',
          background: 'rgba(var(--gold-rgb),.15)', border: '1px solid rgba(var(--gold-rgb),.5)', color: C.gold,
          padding: '2px 6px', borderRadius: 2,
        }}>
          {tag}
        </span>
      )}
      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', color: C.inkFaint, lineHeight: 1.8, textTransform: 'uppercase' }}>
        {children}
      </div>
    </div>
  );
}

function Subhead({ children }) {
  return (
    <div style={{
      fontFamily: MONO, fontSize: 11, letterSpacing: '.2em', textTransform: 'uppercase',
      color: C.inkFaint, margin: '30px 0 4px', borderBottom: `1px solid ${C.lineSoft}`, paddingBottom: 7,
    }}>
      {children}
    </div>
  );
}

function shortSetLabel(name) {
  return name.replace(/^Star Wars\s+/, '');
}

export default function ArtistProfile({ artistSlug, approvalToken }) {
  const [user, setUser] = useState(null);
  const [artist, setArtist] = useState(null);
  const [sets, setSets] = useState([]);
  const [sources, setSources] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [sketchCount, setSketchCount] = useState(0);
  const [ownedCards, setOwnedCards] = useState([]);
  const [sheetFilter, setSheetFilter] = useState(null);
  const [sheetView, setSheetView] = useState(null); // index into the active group's sheets — null = lightbox closed
  const sheetViewMaxRef = useRef(0); // last valid index, kept current by the render below
  const [insertView, setInsertView] = useState(null); // index into insertSlides — same lightbox, for insert set art
  const insertViewMaxRef = useRef(0);
  const [approving, setApproving] = useState(false);
  const [ownedRows, setOwnedRows] = useState(1);
  const [ownedCols, setOwnedCols] = useState(4);
  const ownedGridRef = useRef(null);
  const timelineRef = useRef(null);

  // The release timeline scrolls horizontally on narrow screens; open it at
  // its newest end so the latest release is what collectors see first.
  useEffect(() => {
    const el = timelineRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [sets]);

  // One "row" of collector cards = however many grid columns fit right now.
  useEffect(() => {
    const measure = () => {
      if (!ownedGridRef.current) return;
      if (ownedGridRef.current.getBoundingClientRect().width === 0) return; // not laid out yet
      const cols = getComputedStyle(ownedGridRef.current).gridTemplateColumns.split(' ').filter(Boolean).length;
      if (cols > 0) setOwnedCols(cols);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ownedCards.length]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUser(user));
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      let a = null;
      if (approvalToken) {
        // Approval preview: the page is fetched through a token-keyed
        // security-definer RPC because RLS hides unapproved artists.
        const { data: bundle } = await supabase.rpc('artist_approval_bundle', { p_token: approvalToken });
        if (cancelled) return;
        if (!bundle || !bundle.artist) {
          setNotFound(true);
          setLoading(false);
          document.title = 'Sketch Artists · CollectionKeeper';
          return;
        }
        a = bundle.artist;
        setArtist(a);
        document.title = `Approve your page · ${a.name} · CollectionKeeper`;
        setSets(bundle.sets || []);
        setSources(bundle.sources || []);
        setSheets(bundle.sheets || []);
        setSketchCount(bundle.sketch_count || 0);
        setLoading(false);
      } else {
        const { data, error } = await supabase
          .from('artists')
          .select('*')
          .eq('slug', artistSlug)
          .maybeSingle();
        if (cancelled) return;
        if (error || !data) {
          setNotFound(true);
          setLoading(false);
          document.title = 'Sketch Artists · CollectionKeeper';
          return;
        }
        a = data;
        setArtist(a);
        document.title = `${a.name} · Sketch Artists · CollectionKeeper`;

        const [setsRes, sourcesRes, sheetsRes, sketchesRes] = await Promise.all([
          supabase.from('artist_sets').select('*').eq('artist_id', a.id).order('display_order'),
          supabase.from('artist_sources').select('*').eq('artist_id', a.id),
          supabase.from('artist_sheets').select('*').eq('artist_id', a.id).order('sheet_index'),
          supabase.from('artist_sketches').select('id', { count: 'exact', head: true }).eq('artist_id', a.id),
        ]);
        if (cancelled) return;
        setSets(setsRes.data || []);
        setSources(sourcesRes.data || []);
        setSheets(sheetsRes.data || []);
        setSketchCount(sketchesRes.count || 0);
        setLoading(false);
      }

      // Live collectors feed — RLS already limits this to public profiles.
      const { data: owned } = await supabase
        .from('collections')
        .select('id, user_id, card_key, status, sketch_subject, sketch_parallel, has_photos, photos_updated_at')
        .eq('artist_id', a.id)
        .order('created_at', { ascending: true });
      if (cancelled || !owned || owned.length === 0) return;
      const userIds = [...new Set(owned.map(o => o.user_id))];
      // public_profiles is the anon-safe view — the raw profiles table is self-read-only.
      const { data: owners } = await supabase
        .from('public_profiles')
        .select('id, username')
        .in('id', userIds);
      if (cancelled) return;
      const byId = Object.fromEntries((owners || []).map(o => [o.id, o.username]));
      setOwnedCards(owned.map(o => ({ ...o, username: byId[o.user_id] })).filter(o => o.username));
    }
    load();
    return () => { cancelled = true; };
  }, [artistSlug, approvalToken]);

  // Lightbox (sheets or insert set art): arrow keys page through the open
  // list, Escape closes, and the page behind stops scrolling while it is up.
  useEffect(() => {
    const open = sheetView !== null ? [setSheetView, sheetViewMaxRef]
      : insertView !== null ? [setInsertView, insertViewMaxRef] : null;
    if (!open) return undefined;
    const [setView, maxRef] = open;
    const onKey = (e) => {
      if (e.key === 'Escape') setView(null);
      if (e.key === 'ArrowRight') setView(v => (v === null ? v : Math.min(v + 1, maxRef.current)));
      if (e.key === 'ArrowLeft') setView(v => (v === null ? v : Math.max(v - 1, 0)));
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [sheetView, insertView]);

  const approvePage = async () => {
    setApproving(true);
    const { data: ts, error } = await supabase.rpc('artist_approve', { p_token: approvalToken });
    if (!error && ts) setArtist(prev => ({ ...prev, approved_at: ts }));
    setApproving(false);
  };

  if (loading) {
    return (
      <div className="ap-page" style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{RESPONSIVE_CSS}</style>
        <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '.2em', color: C.inkFaint }}>LOADING PROFILE…</div>
      </div>
    );
  }

  if (notFound || !artist) {
    return (
      <div className="ap-page">
        <style>{RESPONSIVE_CSS}</style>
        {user && <NavDrawer user={user} />}
        <div className="ap-wrap" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: C.inkFaint, marginBottom: 12 }}>
            Resources / Sketch artists
          </div>
          <h1 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', color: '#fff', fontSize: 'clamp(24px, 4vw, 40px)', margin: '0 0 14px' }}>
            Profile not found
          </h1>
          <p style={{ maxWidth: '60ch', color: C.inkDim }}>
            This profile does not exist yet, or it has not been published. We write one profile at a time and check every claim before it goes up.
          </p>
          <a className="ap-btn" href="/resources/artists" style={{ marginTop: 10 }}>All sketch artists</a>
        </div>
      </div>
    );
  }

  const notes = artist.section_notes || {};
  const sig = parseSignature(artist.signature_md);
  // Sketch sets drive the timeline and stats; illustrated insert sets are a
  // different kind of work and render in their own block, never blended in.
  // contribution_type is the canonical split — the same product can appear
  // twice for an artist who both drew sketches for it and illustrated an
  // insert in it (is_insert is the legacy flag, kept in sync).
  const swSets = sets.filter(s => s.contribution_type !== 'illustrated');
  const insertSets = sets.filter(s => s.contribution_type === 'illustrated');
  // Set lists run newest release first, matching the archive chips; the
  // timeline chart alone stays chronological because it carries the year axis.
  const flagshipSets = swSets.filter(s => s.is_flagship).reverse();
  const otherSets = swSets.filter(s => !s.is_flagship).reverse();
  const maxCount = Math.max(...swSets.map(s => s.card_count_stated || 0), 1);
  const totalStated = swSets.reduce((sum, s) => sum + (s.card_count_stated || 0), 0);
  // Once every per-set count is archive-verified the total is a fact, not an
  // estimate; until then it carries the ~ but is never rounded past the truth.
  const allCountsVerified = swSets.length > 0 && swSets.every(s => s.confidence === 'high');
  // No set has a stated production total — bars go flat rather than fake.
  const hasCounts = totalStated > 0;
  // The hero "cards drawn" total only tells the truth when most sets carry a
  // stated count — summing a counted handful would pass off a fraction of a
  // career as the whole. Below half coverage the stat shows a dash instead.
  const countedSets = swSets.filter(s => s.card_count_stated != null).length;
  const countsCoverMost = countedSets >= swSets.length / 2;
  const years = [...new Set(swSets.map(s => s.year).filter(Boolean))].sort();
  const firstChromeId = (swSets.find(s => s.is_chrome) || {}).id;

  const setHref = (s) => (s.set_slug ? `/sets/${s.set_slug}` : null);

  // insert_name is the insert's own name; set_name holds the host product
  // it was released in.
  const insertInfo = (s) => ({
    title: s.insert_name || s.set_name,
    parent: parentSetLabel(s.set_slug) || (s.insert_name ? shortSetLabel(s.set_name) : null),
    count: s.card_count_stated != null ? `${s.card_count_stated} cards` : 'card count not stated',
  });
  // Insert tiles with art open in the lightbox; the rest link to the checklist.
  const insertSlides = insertSets.filter(s => s.sample_image_url);
  insertViewMaxRef.current = Math.max(insertSlides.length - 1, 0);

  // Sheets hold the full-set record; sketches are the cropped/tagged layer on top.
  const archiveCount = sketchCount > 0
    ? sketchCount
    : sheets.reduce((sum, sh) => sum + (sh.card_count_stated || 0), 0);

  // Short display label for a sheet's set, e.g. "Galaxy 2024".
  const sheetSetLabel = (sh) => {
    const parent = swSets.find(s => s.set_slug && s.set_slug === sh.set_slug);
    const name = shortSetLabel(sh.set_name || (parent ? parent.set_name : ''));
    return parent && parent.year ? `${name} ${parent.year}` : name;
  };

  // Archive filter: one group per set the artist has sent sheets for.
  const sheetGroups = [];
  sheets.forEach((sh) => {
    const key = sh.set_slug || sh.set_name || 'unknown';
    let group = sheetGroups.find(g => g.key === key);
    if (!group) {
      group = { key, label: sheetSetLabel(sh), sheets: [], cardCount: 0 };
      sheetGroups.push(group);
    }
    group.sheets.push(sh);
    group.cardCount += sh.card_count_stated || 0;
  });
  // Chips run in release order, most recent first — the newest set leads.
  // Within a year the artist_sets display_order (the timeline sequence) wins;
  // sets without a linked artist_sets row slot mid-year by their label year.
  const groupRank = (g) => {
    const year = parseInt((g.label.match(/(19|20)\d{2}/) || ['0'])[0], 10);
    const parent = swSets.find(s =>
      (s.set_slug && s.set_slug === g.key) ||
      (s.year && `${shortSetLabel(s.set_name)} ${s.year}` === g.label)
    );
    return year * 100 + (parent && parent.display_order != null ? parent.display_order : 50);
  };
  sheetGroups.sort((g1, g2) => groupRank(g2) - groupRank(g1));
  // Lead with the newest year's biggest set, so a handful of cards in a later
  // release doesn't open ahead of the artist's main run that year. Only sets
  // linked to a CK checklist can take the lead.
  const newestYear = sheetGroups.length > 1 ? Math.floor(groupRank(sheetGroups[0]) / 100) : 0;
  const leadPool = sheetGroups.filter(g =>
    g.sheets[0].set_slug && Math.floor(groupRank(g) / 100) === newestYear);
  if (leadPool.length > 0) {
    const lead = leadPool.reduce((best, g) => (g.cardCount > best.cardCount ? g : best));
    sheetGroups.splice(sheetGroups.indexOf(lead), 1);
    sheetGroups.unshift(lead);
  }
  const activeGroup = sheetGroups.find(g => g.key === sheetFilter) || sheetGroups[0];
  sheetViewMaxRef.current = activeGroup ? activeGroup.sheets.length - 1 : 0;

  const mailto = (subject) => `mailto:${CONTACT}?subject=${encodeURIComponent(subject)}`;

  return (
    <div className="ap-page">
      <style>{RESPONSIVE_CSS}</style>
      {user && <NavDrawer user={user} />}
      <div className="ap-wrap" style={{ paddingBottom: 90 }}>

        {/* ── APPROVAL BANNER (token link only) ── */}
        {approvalToken && (
          artist.approved_at ? (
            <div style={{
              margin: '28px 0 0', border: '1px solid rgba(var(--green-rgb),.5)', borderLeft: `3px solid ${C.green}`,
              borderRadius: 3, background: 'linear-gradient(180deg, rgba(var(--green-rgb),.08), transparent)', padding: '20px 24px',
            }}>
              <h3 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 15, color: C.green, margin: '0 0 7px' }}>
                APPROVED — THIS PAGE IS NOW PUBLIC
              </h3>
              <p style={{ margin: 0, maxWidth: '70ch' }}>
                Approved on {new Date(artist.approved_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}. Thank you. Your images remain yours — if you ever want anything changed or removed, one message does it.
              </p>
            </div>
          ) : (
            <div style={{
              margin: '28px 0 0', border: '1px dashed rgba(var(--gold-rgb),.55)', borderLeft: `3px solid ${C.gold}`,
              borderRadius: 3, background: 'linear-gradient(180deg, rgba(var(--gold-rgb),.08), transparent)', padding: '20px 24px',
            }}>
              <h3 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 15, color: C.gold, margin: '0 0 7px' }}>
                THIS PAGE IS WAITING FOR YOUR APPROVAL
              </h3>
              <p style={{ margin: '0 0 8px', maxWidth: '70ch' }}>
                {artist.name.split(' ')[0]}, this is your page exactly as collectors would see it. Nothing goes public until you approve it. Your images stay yours — CK displays them with credit and removes any of them the moment you ask.
              </p>
              <p style={{ margin: '0 0 16px', maxWidth: '70ch', color: C.inkDim }}>
                Review everything below. If something is wrong, ask for changes and we fix it before anything goes live.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="ap-btn solid"
                  onClick={approvePage}
                  disabled={approving}
                  style={{ opacity: approving ? 0.6 : 1 }}
                >
                  {approving ? 'Approving…' : 'Approve this page'}
                </button>
                <a className="ap-btn ghost" href={mailto(`Changes to my page — ${artist.name}`)}>Request changes</a>
              </div>
            </div>
          )
        )}

        {/* ── HERO ── */}
        <div style={{ padding: '52px 0 30px', borderBottom: `1px solid ${C.lineSoft}` }}>
          <div className="ap-clear-nav" style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: C.inkFaint }}>
            <a href="/resources" style={{ color: C.inkFaint, textDecoration: 'none' }}>Resources</a>
            {' / '}
            <a href="/resources/artists" style={{ color: C.inkFaint, textDecoration: 'none' }}>Sketch artists</a>
            {' / '}{artist.name.split(' ').slice(-1)[0]}
          </div>
          <div className="ap-hero-grid">
            <div>
              <h1 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 'clamp(31px, 6.4vw, 62px)', fontWeight: 900, lineHeight: 0.94, color: '#fff', margin: '10px 0 0' }}>
                {artist.name.split(' ').slice(0, -1).join(' ')}
                <em style={{ display: 'block', fontStyle: 'normal', color: C.gold }}>{artist.name.split(' ').slice(-1)[0]}</em>
              </h1>
              {artist.hero_sub && (
                <p style={{ fontSize: 19, color: C.inkDim, margin: '14px 0 0', maxWidth: '52ch' }}>{artist.hero_sub}</p>
              )}
              {((artist.badges || []).length > 0 || artist.approved_at) && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 18 }}>
                  {artist.approved_at && (
                    <span style={{
                      fontFamily: MONO, fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase',
                      padding: '5px 10px', borderRadius: 2,
                      background: 'rgba(var(--green-rgb),.07)', border: '1px solid rgba(var(--green-rgb),.5)', color: C.green,
                    }}>
                      ✓ Approved by the artist
                    </span>
                  )}
                  {(artist.badges || []).map((b, i) => {
                    const official = /official/i.test(b);
                    return (
                      <span key={i} style={{
                        fontFamily: MONO, fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase',
                        padding: '5px 10px', borderRadius: 2,
                        background: official ? 'rgba(var(--gold-rgb),.07)' : 'rgba(var(--wash-rgb),.04)',
                        border: `1px solid ${official ? 'rgba(var(--gold-rgb),.45)' : C.lineSoft}`,
                        color: official ? C.gold : C.inkDim,
                      }}>
                        {b}
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="ap-stats">
                {[
                  // For an artist who also illustrates inserts, the sets stat
                  // counts sketch sets only — anything else inflates it.
                  { num: String(swSets.length), label: insertSets.length > 0 ? 'Sketch sets' : 'Star Wars sets' },
                  hasCounts && countsCoverMost
                    ? { num: `${allCountsVerified ? '' : '~'}${totalStated.toLocaleString('en-US')}`, label: 'Cards drawn' }
                    : insertSets.length > 0
                      ? { num: String(insertSets.length), label: 'Sets illustrated' }
                      : { num: '—', label: 'Cards drawn' },
                  { num: artist.debut_year ? String(artist.debut_year) : '—', label: 'Topps debut' },
                  { num: archiveCount > 0 ? archiveCount.toLocaleString('en-US') : '—', label: 'In the archive' },
                ].map((s, i) => (
                  <div key={i} style={{ background: C.card, padding: '14px 16px' }}>
                    <b style={{ display: 'block', fontFamily: MONO, fontSize: 'clamp(19px, 3vw, 28px)', color: C.cyan, lineHeight: 1.1, fontWeight: 400 }}>{s.num}</b>
                    <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: C.inkFaint }}>{s.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 26 }}>
                <a className="ap-btn solid" href="#archive">Browse the archive</a>
                <a className="ap-btn" href="#collectors">Who owns one</a>
                <a className="ap-btn ghost" href="#signature">How they sign</a>
              </div>
            </div>
            <div className="ap-portrait">
              {artist.portrait_url ? (
                <div style={{ aspectRatio: '2.5 / 3.5', background: C.card, border: `1px solid ${C.line}`, borderRadius: 3, overflow: 'hidden' }}>
                  <img src={artist.portrait_url} alt={artist.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
              ) : (
                <CardFrame>Artist photo<br />coming with permission</CardFrame>
              )}
              <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', color: C.inkFaint, marginTop: 9, textTransform: 'uppercase' }}>
                Every image on this page sits in card proportion
              </div>
            </div>
          </div>

          {/* Primer — beginner audience, do not remove */}
          <div style={{
            margin: '30px 0 0', background: 'linear-gradient(180deg, rgba(var(--purple-rgb),.09), rgba(var(--purple-rgb),.02))',
            border: '1px solid rgba(var(--purple-rgb),.3)', borderLeft: `3px solid ${C.purple}`, borderRadius: 3, padding: '18px 22px',
          }}>
            <h3 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 13, fontWeight: 700, color: C.purple, margin: '0 0 7px' }}>
              NEW TO SKETCH CARDS?
            </h3>
            <p style={{ margin: 0, maxWidth: '74ch' }}>
              A sketch card is a real, hand-drawn miniature artwork made directly on a blank trading card, then sealed into packs at random. No two are alike, which is why collectors call them 1/1 — one of one. Pull one and you own the only copy that exists.
            </p>
          </div>
        </div>

        {/* ── BIOGRAPHY ── */}
        <ProvenanceSection title="Who they are" confidence={artist.bio_confidence} editorNote={notes.bio}>
          {paragraphs(artist.bio_md).map((p, i) => (
            <p key={i} style={{ maxWidth: '70ch' }}>{p}</p>
          ))}
        </ProvenanceSection>

        {/* ── SETS ── */}
        <ProvenanceSection id="sets" title="Sketch sets they have drawn" confidence={artist.sets_confidence}>
          <p style={{ maxWidth: '70ch' }}>
            Every card in this section is hand-drawn and one of one — a single physical card that exists nowhere else.
          </p>
          <p style={{ maxWidth: '70ch' }}>
            {hasCounts && countsCoverMost
              ? `Every bar below is a Topps Star Wars set ${artist.name.split(' ').slice(-1)[0]} contributed original sketch cards to. Height shows roughly how many cards they produced for that release — useful when you are working out how hard a particular ${artist.name.split(' ').slice(-1)[0]} is to find.`
              : hasCounts
                ? `Every bar below is a Topps Star Wars set ${artist.name.split(' ').slice(-1)[0]} contributed original sketch cards to. A bar gets height only where a production total has been stated — most of these sets have none, so most bars sit flat, and we do not guess.`
                : `Every bar below is a Topps Star Wars set ${artist.name.split(' ').slice(-1)[0]} contributed original sketch cards to. Height would normally show how many cards they produced per release — here every bar sits flat, because no per-set count has been stated by Topps or by the artist, and we do not guess.`}
          </p>

          <div ref={timelineRef} style={{ margin: '24px 0 8px', border: `1px solid ${C.lineSoft}`, background: C.card, borderRadius: 3, padding: '22px 20px 14px', overflowX: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, minWidth: 660, height: 170 }}>
              {swSets.map((s) => {
                const h = s.card_count_stated
                  ? Math.max(8, Math.round((s.card_count_stated / maxCount) * 100))
                  : (hasCounts ? 8 : 24);
                const href = setHref(s);
                const bar = (
                  <div className="ap-tl-item" key={s.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 9, minWidth: 52, height: '100%', cursor: href ? 'pointer' : 'default' }}>
                    <span style={{ fontFamily: MONO, fontSize: 11, color: C.inkDim }}>{s.card_count_stated ?? '—'}</span>
                    <div className="ap-tl-bar" style={{
                      width: '100%', maxWidth: 46, height: `${h}%`, borderRadius: '2px 2px 0 0', transition: '.2s',
                      background: s.is_chrome
                        ? 'linear-gradient(180deg, #fff, rgba(var(--cyan-rgb),.3))'
                        : `linear-gradient(180deg, ${C.cyan}, rgba(var(--cyan-rgb),.22))`,
                    }} />
                    <span style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '.05em', textTransform: 'uppercase', color: C.inkFaint, textAlign: 'center', lineHeight: 1.35, minHeight: 26 }}>
                      {shortSetLabel(s.set_name)}{s.is_chrome && s.id === firstChromeId ? <><br />debut</> : ''}
                    </span>
                  </div>
                );
                return href ? (
                  <a key={s.id} href={href} style={{ display: 'contents', color: 'inherit', textDecoration: 'none' }}>{bar}</a>
                ) : bar;
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${C.lineSoft}`, marginTop: 10, paddingTop: 8, minWidth: 660 }}>
              {years.map(y => (
                <span key={y} style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', color: C.inkFaint }}>{y}</span>
              ))}
            </div>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: '.06em', color: C.inkFaint, marginTop: 10 }}>
            {hasCounts ? '' : 'FLAT BARS = COUNT NOT STATED · '}WHITE-TOPPED BARS = CHROME RELEASES · TAP ANY SET TO OPEN ITS CHECKLIST
          </div>

          <Subhead>Flagship sets — base and chrome</Subhead>
          <div className="ap-setgrid">
            {flagshipSets.map(s => <SetRow key={s.id} s={s} href={setHref(s)} />)}
          </div>

          <Subhead>Other Topps Star Wars sets</Subhead>
          <div className="ap-setgrid">
            {otherSets.map(s => <SetRow key={s.id} s={s} href={setHref(s)} />)}
          </div>

          {insertSets.length > 0 && (
            <p style={{ maxWidth: '70ch', marginTop: 18, color: C.inkDim }}>
              Everything above is hand-drawn 1/1 sketch work. {artist.name.split(' ').slice(-1)[0]} also illustrates printed insert sets for Topps — a different job entirely, with its own section below.
            </p>
          )}

          {(artist.other_work || []).length > 0 && (
            <>
              <Subhead>Beyond Star Wars — for context only</Subhead>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 14 }}>
                {artist.other_work.map((w, i) => (
                  <span key={i} style={{
                    fontFamily: MONO, fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase',
                    padding: '5px 10px', borderRadius: 2, background: 'rgba(var(--wash-rgb),.03)',
                    border: `1px solid ${C.lineSoft}`, color: C.inkFaint,
                  }}>
                    {w}
                  </span>
                ))}
              </div>
            </>
          )}
          <div style={{
            fontFamily: MONO, fontSize: 11.5, letterSpacing: '.04em', lineHeight: 1.7, color: C.inkFaint,
            maxWidth: '78ch', marginTop: 18, borderTop: '1px dashed rgba(var(--wash-rgb),0.07)', paddingTop: 12,
          }}>
            EDITOR'S NOTE — {notes.sets}
          </div>
        </ProvenanceSection>

        {/* ── ILLUSTRATED INSERT SETS — only for artists who also do production art ── */}
        {insertSets.length > 0 && (
          <ProvenanceSection
            id="illustrated"
            title="Sets they have illustrated"
            confidence={artist.illustrated_confidence
              || (insertSets.some(s => s.confidence === 'low') ? 'low' : insertSets.some(s => s.confidence === 'med') ? 'med' : 'high')}
            badge="Printed sets — not sketches"
            editorNote={notes.inserts}
          >
            <p style={{ maxWidth: '70ch' }}>
              Not everything {artist.name.split(' ').slice(-1)[0]} makes for Topps is a sketch card. Topps also commissions them to illustrate whole insert sets — printed cards, built from their artwork, that go into packs by the case. Knowing the difference is knowing what you are holding.
            </p>
            <div className="ap-ladder" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', maxWidth: 760 }}>
              {[
                { tone: C.gold, label: 'A sketch card', text: 'Drawn by hand, directly on the card. One copy exists anywhere in the world.' },
                { tone: C.purple, label: 'An insert card', text: 'Printed from commissioned artwork. Every copy shows the same art, collected by card number.' },
              ].map((r, i) => (
                <div key={i} style={{ background: C.card, border: `1px solid ${C.lineSoft}`, borderLeft: `3px solid ${r.tone}`, borderRadius: 2, padding: '12px 14px' }}>
                  <b style={{ display: 'block', fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: r.tone, marginBottom: 4, fontWeight: 400 }}>{r.label}</b>
                  <span style={{ fontSize: 14.5, color: C.inkDim, lineHeight: 1.4 }}>{r.text}</span>
                </div>
              ))}
            </div>
            <Subhead>Commissioned insert sets — tap a set to see every card</Subhead>
            <div className="ap-stylesamples">
              {insertSets.map(s => {
                const href = setHref(s);
                const { title: insertTitle, parent, count } = insertInfo(s);
                const slideIdx = insertSlides.indexOf(s);
                const tile = (
                  <div className="ap-stylesample" style={{ height: '100%' }}>
                    <div style={{ aspectRatio: '2.5 / 3.5', overflow: 'hidden', background: C.raised, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {s.sample_image_url ? (
                        <img
                          src={s.sample_image_url}
                          alt={`${insertTitle} insert card${s.sample_subject ? ` — ${s.sample_subject}` : ''}`}
                          loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', color: C.inkFaint, lineHeight: 1.8, textTransform: 'uppercase', textAlign: 'center', padding: 16 }}>
                          Card art<br />after release
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '9px 11px', borderTop: `1px solid ${C.lineSoft}` }}>
                      <div style={{ fontFamily: DISPLAY, fontSize: 12, fontWeight: 700, color: '#fff', textTransform: 'uppercase', lineHeight: 1.3 }}>{insertTitle}</div>
                      <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', color: C.inkFaint, marginTop: 4 }}>
                        {s.year}{parent ? ` · In ${parent}` : ''} · {count}{!href && ' · no CK checklist yet'}
                      </div>
                      {s.sample_subject && (
                        <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', color: C.cyan, marginTop: 3 }}>{s.sample_subject}</div>
                      )}
                    </div>
                  </div>
                );
                if (slideIdx >= 0) {
                  return (
                    <a
                      key={s.id}
                      href={s.sample_image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => { e.preventDefault(); setInsertView(slideIdx); }}
                      style={{ textDecoration: 'none' }}
                    >{tile}</a>
                  );
                }
                return href
                  ? <a key={s.id} href={href} style={{ textDecoration: 'none' }}>{tile}</a>
                  : <div key={s.id}>{tile}</div>;
              })}
            </div>
          </ProvenanceSection>
        )}

        {/* ── SIGNATURE — rail color tracks how much evidence we hold ── */}
        <ProvenanceSection id="signature" title="How they sign a card" confidence={artist.signature_confidence} editorNote={notes.signature}>
          <p style={{ maxWidth: '70ch' }}>
            {sig.intro || 'This is the one thing we could not pin down, so we are not going to guess at it. Here is exactly what we know and what we still need.'}
          </p>
          {(() => {
            const accent = artist.signature_confidence === 'high' ? C.green
              : artist.signature_confidence === 'med' ? C.gold : C.red;
            const accentBorder = artist.signature_confidence === 'high' ? 'rgba(var(--green-rgb),.5)'
              : artist.signature_confidence === 'med' ? 'rgba(var(--gold-rgb),.5)' : 'rgba(var(--red-2-rgb),.5)';
            const sigImages = (artist.signature_images || []).slice(0, 4);
            return (
              <>
                <div className="ap-sigimgs">
                  {sigImages.map((url, i) => (
                    <a key={i} className="ap-sigimg" href={url} target="_blank" rel="noopener noreferrer">
                      <div style={{ aspectRatio: '2.5 / 3.5', overflow: 'hidden' }}>
                        <img
                          src={url}
                          alt={`Back of a hand-signed ${artist.name} sketch card — sample ${i + 1}`}
                          loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </div>
                      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: C.inkFaint, padding: '8px 10px', borderTop: `1px solid ${C.lineSoft}` }}>
                        Confirmed card back · {i + 1} of {sigImages.length}
                      </div>
                    </a>
                  ))}
                  {sigImages.length < 4 && (
                    <a href={mailto(`Card back photo — ${artist.name}`)} style={{ textDecoration: 'none' }}>
                      <div style={{
                        aspectRatio: '2.5 / 3.5', background: 'rgba(var(--wash-rgb),.02)',
                        border: `1px dashed ${accentBorder}`, borderRadius: 3,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 16,
                      }}>
                        <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', color: C.inkFaint, lineHeight: 1.8, textTransform: 'uppercase' }}>
                          {sigImages.length === 0 ? <>Card back<br />photo needed</> : <>Your card back here<br />send a photo,<br />get credited</>}
                        </div>
                      </div>
                    </a>
                  )}
                </div>
                <div className="ap-gap" style={{
                  gridTemplateColumns: 'minmax(0,1fr)',
                  border: `1px dashed ${accentBorder}`,
                  background: `linear-gradient(180deg, ${artist.signature_confidence === 'med' ? 'rgba(var(--gold-rgb),.06)' : artist.signature_confidence === 'high' ? 'rgba(var(--green-rgb),.06)' : 'rgba(var(--red-2-rgb),.07)'}, transparent)`,
                }}>
                  <div>
                    <h3 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 15, color: accent, margin: '0 0 8px' }}>WHAT WE CAN CONFIRM</h3>
                    <ul style={{ margin: '0 0 16px', paddingLeft: 18, color: C.inkDim }}>
                      {sig.knowns.map((k, i) => <li key={i} style={{ marginBottom: 4 }}>{k}</li>)}
                    </ul>
                    {sig.need && (
                      <>
                        <h3 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 15, color: accent, margin: '0 0 8px' }}>WHAT WE STILL NEED</h3>
                        <p style={{ margin: '0 0 14px', maxWidth: '60ch' }}>{sig.need}</p>
                      </>
                    )}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <a className="ap-btn solid" href={mailto(`Card back photo — ${artist.name}`)}>Send a card back</a>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </ProvenanceSection>

        {/* ── STYLE ── */}
        <ProvenanceSection title="How to recognize their work" confidence={artist.style_confidence} editorNote={notes.style}>
          {paragraphs(artist.style_md).map((p, i) => (
            <p key={i} style={{ maxWidth: '70ch' }}>{p}</p>
          ))}
          {(artist.style_traits || []).length > 0 && (
            <div className="ap-traits">
              {artist.style_traits.map((t, i) => (
                <div key={i} style={{ background: C.card, border: `1px solid ${C.lineSoft}`, borderTop: `2px solid ${C.purple}`, borderRadius: 2, padding: 14 }}>
                  <b style={{ display: 'block', fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: C.purple, marginBottom: 5, fontWeight: 400 }}>{t.label}</b>
                  <span style={{ fontSize: 15, lineHeight: 1.4 }}>{t.value}</span>
                </div>
              ))}
            </div>
          )}
          {(artist.style_samples || []).length > 0 && (
            <>
              <Subhead>Sample cards — tap to view full size</Subhead>
              <div className="ap-stylesamples">
                {/* Landscape cards get a two-column tile at card proportion
                    instead of a center crop that cuts the scene in half. */}
                {artist.style_samples.slice(0, 4).map((s, i) => (
                  <a key={i} className={`ap-stylesample${s.orientation === 'landscape' ? ' wide' : ''}`} href={s.image_url} target="_blank" rel="noopener noreferrer">
                    <div style={{ aspectRatio: s.orientation === 'landscape' ? '3.5 / 2.5' : '2.5 / 3.5', overflow: 'hidden' }}>
                      <img
                        src={s.image_url}
                        alt={`${artist.name} sketch card sample${s.subject ? ` — ${s.subject}` : ''}`}
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                    <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.lineSoft}` }}>
                      <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: C.ink }}>{s.subject}</span>
                      {s.set_label && (
                        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: C.inkFaint }}> · {s.set_label}</span>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}
        </ProvenanceSection>

        {/* ── ARTIST ARCHIVE ── */}
        <ProvenanceSection
          id="archive"
          title="The artist's own archive"
          confidence={sheets.length > 0 ? 'high' : undefined}
          badge={sheets.length > 0 ? 'Verified — sent by the artist' : 'In progress — sheets requested'}
          editorNote="Artist-supplied images are the highest tier of provenance on CK: no identification guesswork, no cropping from auction photos. Sheets go live on arrival; card-level tagging follows and is never a condition of publishing."
        >
          <p style={{ maxWidth: '70ch' }}>
            Card imagery on this page comes from three separate tiers, and we never blend them. The artist's own archive is the top tier — a complete record of what they drew for a set, including the cards still sealed in packs somewhere, waiting to be pulled.
          </p>

          {sheets.length === 0 ? (
            <div style={{
              marginTop: 22, border: `1px dashed ${C.line}`, borderRadius: 3, padding: '22px 24px',
              background: 'rgba(var(--wash-rgb),.02)',
            }}>
              <p style={{ margin: 0, maxWidth: '66ch', color: C.inkDim }}>
                We have asked {artist.name.split(' ').slice(-1)[0]} for their sheets — the composite images artists photograph before their cards go back to Topps. They publish here the moment they arrive. Naming and cropping each card happens afterwards, and never holds anything up.
              </p>
            </div>
          ) : (
            <>
              <div className="ap-fchips">
                {sheetGroups.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    className={`ap-fchip${activeGroup && activeGroup.key === g.key ? ' on' : ''}`}
                    onClick={() => { setSheetFilter(g.key); setSheetView(null); }}
                  >
                    {g.label} · {g.cardCount}
                  </button>
                ))}
              </div>
              <Subhead>
                As the artist sent them — {activeGroup.label} · {activeGroup.sheets.length} {activeGroup.sheets.length === 1 ? 'sheet' : 'sheets'} · {activeGroup.cardCount} {activeGroup.cardCount === 1 ? 'card' : 'cards'}
              </Subhead>
              <div className="ap-sheets">
                {activeGroup.sheets.map((sh, shIdx) => {
                  const tagged = 0; // card-level tagging lands with the crops phase
                  return (
                    <a
                      key={sh.id}
                      className="ap-sheetcard"
                      href={sh.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => { e.preventDefault(); setSheetView(shIdx); }}
                    >
                      <div style={{ position: 'relative', borderBottom: `1px solid ${C.lineSoft}`, background: C.raised }}>
                        <img
                          src={sh.image_url}
                          alt={`${sheetSetLabel(sh)} — sheet ${sh.sheet_index} of ${sh.sheet_total}, as sent by the artist`}
                          loading="lazy"
                          style={{ width: '100%', display: 'block' }}
                        />
                        <span style={{
                          position: 'absolute', top: 8, right: 8, fontFamily: MONO, fontSize: 9.5, letterSpacing: '.09em',
                          background: 'rgba(var(--bg-rgb),.85)', border: `1px solid ${C.lineSoft}`, color: C.inkDim,
                          padding: '2px 6px', borderRadius: 2,
                        }}>
                          {sh.sheet_index} / {sh.sheet_total}
                        </span>
                      </div>
                      <div style={{ padding: '11px 12px 13px' }}>
                        <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '.13em', textTransform: 'uppercase', color: C.cyan }}>
                          {sheetSetLabel(sh)}
                        </div>
                        <h4 style={{ fontFamily: DISPLAY, fontSize: 13, fontWeight: 700, color: '#fff', margin: '5px 0 8px', textTransform: 'uppercase' }}>
                          Sheet {sh.sheet_index}
                        </h4>
                        <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', textTransform: 'uppercase', color: C.inkFaint }}>
                          {sh.card_count_stated} cards · {tagged} tagged
                        </div>
                        <div style={{ height: 3, background: C.lineSoft, borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
                          <i style={{ display: 'block', height: '100%', width: `${sh.card_count_stated ? Math.round((tagged / sh.card_count_stated) * 100) : 0}%`, background: C.green, borderRadius: 2 }} />
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: C.inkFaint, marginTop: 12 }}>
                Sheets publish the moment they arrive — tagging individual cards happens afterwards, and never blocks the artist
              </div>
            </>
          )}

          <div style={{
            marginTop: 24, border: '1px solid rgba(var(--green-rgb),.3)', borderLeft: `3px solid ${C.green}`,
            borderRadius: 3, background: 'linear-gradient(180deg, rgba(var(--green-rgb),.07), transparent)', padding: '20px 22px',
          }}>
            <h3 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 14, color: C.green, margin: '0 0 7px' }}>
              ARTISTS — SEND US YOUR SET
            </h3>
            <p style={{ margin: '0 0 14px', maxWidth: '66ch' }}>
              Send your sheets exactly as you already post them. Drop in the images, tell us the set, and we build the archive page for you. Your name stays on every image, you keep the rights, and collectors get a permanent record of work that otherwise scatters across a thousand auction listings.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a className="ap-btn solid" href={mailto(`Artist sheets — ${artist.name}`)} style={{ padding: '10px 16px' }}>Send your sheets</a>
            </div>
          </div>
        </ProvenanceSection>

        {/* ── IN COLLECTORS' HANDS ── */}
        <ProvenanceSection
          id="collectors"
          title="In collectors' hands"
          confidence={ownedCards.length > 0 ? 'high' : undefined}
          badge={ownedCards.length > 0 ? 'Live — from public CK profiles' : 'Coming soon — from public CK profiles'}
          editorNote={ownedCards.length > 0
            ? 'Fed automatically from collection entries on profiles set to public. No editor involved — tagging a sketch card with its artist is what places it here.'
            : 'This section will feed automatically from collection entries on profiles set to public, with no editor involved. It goes live once sketch card entries can carry an artist.'}
        >
          <p style={{ maxWidth: '70ch' }}>
            {ownedCards.length > 0
              ? `These ${artist.name.split(' ').slice(-1)[0]} cards have been pulled, logged, and shared by CK collectors who set their profiles to public. This section updates itself — no editor involved. Tap a keeper to see the rest of their collection.`
              : `This is where ${artist.name.split(' ').slice(-1)[0]} cards pulled, logged, and shared by CK collectors will appear — credited to the keeper, with their trade status showing. It fills itself from public profiles. No editor involved.`}
          </p>
          <div className="ap-owned" ref={ownedGridRef}>
            {ownedCards.slice(0, ownedRows * ownedCols).map((o) => {
              const parts = (o.card_key || '').split('__');
              const slug = parts[0];
              const number = parts[2] || '';
              const parent = swSets.find(s => s.set_slug === slug);
              const setLabel = parent
                ? `${shortSetLabel(parent.set_name)} ${parent.year}${o.sketch_parallel ? ` · ${o.sketch_parallel}` : ''}`
                : slug;
              const status = {
                collected: { color: C.green, label: 'Collected' },
                trade: { color: C.cyan, label: 'Open to trade' },
                for_sale: { color: C.purple, label: 'For sale' },
                wishlist: { color: C.gold, label: 'Wishlist' },
              }[o.status] || { color: C.inkDim, label: o.status };
              return (
                <article key={o.id} className="ap-owncard">
                  <div style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
                    {o.has_photos ? (
                      <div style={{ aspectRatio: '2.5 / 3.5', overflow: 'hidden', position: 'relative' }}>
                        <span style={{ position: 'absolute', top: 9, right: 9, zIndex: 2, fontFamily: MONO, fontSize: 9.5, letterSpacing: '.1em', background: 'rgba(var(--gold-rgb),.15)', border: '1px solid rgba(var(--gold-rgb),.5)', color: C.gold, padding: '2px 6px', borderRadius: 2 }}>1/1</span>
                        <img
                          src={imageUrl(o.user_id, o.card_key, 'front', o.photos_updated_at)}
                          alt={`${o.sketch_subject || 'Sketch card'} — collector photo`}
                          loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </div>
                    ) : (
                      <CardFrame tag="1/1">Collector photo</CardFrame>
                    )}
                  </div>
                  <div style={{ padding: '11px 13px 13px' }}>
                    <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '.13em', textTransform: 'uppercase', color: C.cyan }}>{setLabel}</div>
                    <h4 style={{ fontFamily: DISPLAY, fontSize: 13, fontWeight: 700, color: '#fff', margin: '5px 0 9px', textTransform: 'uppercase', lineHeight: 1.25 }}>
                      {o.sketch_subject || `Sketch ${number}`}
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 9, borderTop: `1px solid ${C.lineSoft}` }}>
                      <span style={{ width: 21, height: 21, borderRadius: '50%', background: `linear-gradient(135deg, ${C.cyan}, ${C.purple})`, flex: 'none' }} />
                      <a href={`/u/${o.username}`} style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: '.06em', color: C.cyan, textDecoration: 'none' }}>{o.username}</a>
                      <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 8.5, letterSpacing: '.1em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 2, border: `1px solid ${status.color}`, color: status.color }}>
                        {status.label}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
            {ownedRows * ownedCols >= ownedCards.length && (
              <div style={{
                border: `1px dashed ${C.line}`, borderRadius: 3, padding: 18,
                display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 11,
                background: 'rgba(var(--wash-rgb),.02)',
              }}>
                <p style={{ margin: 0, fontSize: 14.5, color: C.inkDim }}>
                  Own {/^[aeiou]/i.test(artist.name.split(' ').slice(-1)[0]) ? 'an' : 'a'} {artist.name.split(' ').slice(-1)[0]}? Set your profile to public and it shows up here, credited to you.
                </p>
                <a className="ap-btn ghost" href="/profile" style={{ padding: '8px 14px' }}>Go to my profile</a>
              </div>
            )}
          </div>
          {ownedRows * ownedCols < ownedCards.length && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                type="button"
                className="ap-btn ghost"
                onClick={() => setOwnedRows(r => r + 1)}
              >
                Load more ({ownedCards.length - ownedRows * ownedCols})
              </button>
            </div>
          )}
        </ProvenanceSection>

        {/* ── WHERE TO FIND THEM ── */}
        <ProvenanceSection
          id="connect"
          title="Where to find them"
          confidence="high"
          badge="From the artist's own channels"
          editorNote={notes.connect}
        >
          <p style={{ maxWidth: '70ch' }}>
            Sketch cards come out of packs, but the person who drew them is reachable. These are {artist.name.split(' ').slice(-1)[0]}'s own channels — the best places to see new work, ask questions, and buy directly from the source.
          </p>
          <div className="ap-connect">
            {[
              artist.website && {
                label: 'Website',
                value: artist.website.replace(/^https?:\/\//, '').replace(/\/$/, ''),
                url: artist.website,
              },
              artist.instagram && {
                label: 'Instagram',
                value: `@${artist.instagram}`,
                url: `https://www.instagram.com/${artist.instagram}`,
              },
              artist.contact_email && {
                label: 'Contact',
                value: artist.contact_email,
                url: `mailto:${artist.contact_email}`,
              },
              ...(artist.links || []).map(l => ({ label: l.label, value: l.note || l.url.replace(/^https?:\/\//, ''), url: l.url })),
            ].filter(Boolean).map((l, i) => (
              <a key={i} className="ap-connectcard" href={l.url} target="_blank" rel="noopener noreferrer">
                <b style={{ display: 'block', fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: C.cyan, marginBottom: 5, fontWeight: 400 }}>{l.label}</b>
                <span style={{ fontSize: 15.5, fontWeight: 600, color: C.ink, lineHeight: 1.4, wordBreak: 'break-word' }}>{l.value}</span>
              </a>
            ))}
          </div>
          {(() => {
            const open = artist.commissions_open;
            const tone = open === true ? C.green : open === false ? C.inkFaint : C.gold;
            const toneBorder = open === true ? 'rgba(var(--green-rgb),.4)' : open === false ? 'rgba(var(--wash-rgb),.12)' : 'rgba(var(--gold-rgb),.45)';
            return (
              <div style={{
                marginTop: 16, border: `1px ${open == null ? 'dashed' : 'solid'} ${toneBorder}`, borderLeft: `3px solid ${tone}`,
                borderRadius: 3, padding: '16px 18px', maxWidth: 640, background: 'rgba(var(--wash-rgb),.02)',
              }}>
                <b style={{ display: 'block', fontFamily: MONO, fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase', color: tone, marginBottom: 6, fontWeight: 400 }}>
                  {open === true ? 'Open to private commissions'
                    : open === false ? 'Not taking commissions right now'
                    : 'Private commissions — unconfirmed'}
                </b>
                <p style={{ margin: 0, fontSize: 14.5, color: C.inkDim, maxWidth: '60ch' }}>
                  {open === true
                    ? 'The artist takes private commission work. Reach out through the channels above to ask about availability and pricing.'
                    : open === false
                    ? 'The artist is not taking private commission requests at the moment. Their channels above are still the place to follow new work.'
                    : `We have not confirmed whether ${artist.name.split(' ').slice(-1)[0]} takes private commissions. Ask on their channels — and if you learn the answer, tell us and we will update this page.`}
                </p>
              </div>
            );
          })()}
        </ProvenanceSection>


        {/* ── EDITOR FOOTER ── */}
        <div style={{ marginTop: 44, border: `1px solid ${C.lineSoft}`, borderRadius: 3, background: C.card, padding: '22px 24px' }}>
          <h3 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 13, color: C.inkDim, margin: '0 0 12px' }}>WHERE THIS CAME FROM</h3>
          <div style={{ fontFamily: MONO, fontSize: 11.5, color: C.inkFaint, lineHeight: 1.9, textTransform: 'uppercase' }}>
            {artist.approved_at && (
              <>APPROVED BY THE ARTIST — {new Date(artist.approved_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}<br /></>
            )}
            {artist.last_verified_at && (
              <>LAST CHECKED — {new Date(artist.last_verified_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}<br /></>
            )}
            {sources.length > 0 && (
              <>SOURCES — {sources.map(s => s.label).join(' · ')}<br /></>
            )}
            {artist.disambiguation && <>{artist.disambiguation}</>}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.lineSoft}` }}>
            <a className="ap-btn ghost" href={mailto(`Correction — ${artist.name} profile`)} style={{ padding: '8px 14px' }}>Report a correction</a>
            <a className="ap-btn ghost" href={mailto(`Card photo — ${artist.name}`)} style={{ padding: '8px 14px' }}>Submit a card photo</a>
            <span style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase', padding: '5px 10px', borderRadius: 2, background: 'rgba(var(--wash-rgb),.04)', border: `1px solid ${C.lineSoft}`, color: C.inkDim }}>
              Free, ad-free, fee-free. Forever.
            </span>
          </div>
        </div>

      </div>

      {/* ── LIGHTBOX — tap a sheet (or an insert set's art) to open it, arrows page through ── */}
      {(() => {
        const lb = sheetView !== null && activeGroup && activeGroup.sheets[sheetView]
          ? {
            index: sheetView, setView: setSheetView, unit: 'sheet',
            items: activeGroup.sheets.map((sh, i) => ({
              src: sh.image_url,
              label: sheetSetLabel(sh),
              title: `${sheetSetLabel(sh)} — sheet ${sh.sheet_index} of ${sh.sheet_total}`,
              alt: `${sheetSetLabel(sh)} — sheet ${sh.sheet_index} of ${sh.sheet_total}, as sent by the artist`,
              meta: [`Sheet ${i + 1} of ${activeGroup.sheets.length}`, `${sh.card_count_stated} cards`],
            })),
          }
          : insertView !== null && insertSlides[insertView]
            ? {
              index: insertView, setView: setInsertView, unit: 'set',
              items: insertSlides.map((s) => {
                const { title, parent, count } = insertInfo(s);
                return {
                  src: s.sample_image_url,
                  label: title,
                  title: `${title} — insert set`,
                  alt: `${title} — every card in the insert set, art by ${artist.name}`,
                  meta: [`${s.year}${parent ? ` · In ${parent}` : ''}`, count],
                  href: setHref(s),
                };
              }),
            }
            : null;
        if (!lb) return null;
        const { index, setView, unit } = lb;
        const item = lb.items[index];
        const last = lb.items.length - 1;
        const navBtn = (disabled) => ({
          fontFamily: MONO, fontSize: 26, lineHeight: 1, color: disabled ? C.inkFaint : '#fff',
          background: 'rgba(var(--bg-rgb),.8)', border: `1px solid ${disabled ? C.lineSoft : 'rgba(var(--gold-rgb),.5)'}`,
          borderRadius: 3, width: 44, height: 64, cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.35 : 1, flexShrink: 0,
        });
        return (
          <div
            role="dialog"
            aria-label={item.title}
            onClick={() => setView(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(var(--bg-rgb),.93)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '16px 8px',
            }}
          >
            <button
              type="button"
              aria-label={`Previous ${unit}`}
              disabled={index === 0}
              onClick={(e) => { e.stopPropagation(); setView(v => Math.max(v - 1, 0)); }}
              style={navBtn(index === 0)}
            >‹</button>
            <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 'min(92vw, 760px)', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
              <img
                src={item.src}
                alt={item.alt}
                style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain', display: 'block', border: `1px solid ${C.lineSoft}`, borderRadius: 3, background: C.card }}
              />
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', justifyContent: 'center',
                fontFamily: MONO, fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: C.inkDim, marginTop: 12,
              }}>
                <span style={{ color: C.cyan }}>{item.label}</span>
                {item.meta.map((m, i) => <span key={i}>{m}</span>)}
                {item.href && <a href={item.href} style={{ color: C.gold, textDecoration: 'none' }}>Open checklist</a>}
                <a href={item.src} target="_blank" rel="noopener noreferrer" style={{ color: C.gold, textDecoration: 'none' }}>Open full size</a>
                <button
                  type="button"
                  onClick={() => setView(null)}
                  style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: C.inkDim, background: 'none', border: `1px solid ${C.lineSoft}`, borderRadius: 2, padding: '4px 10px', cursor: 'pointer' }}
                >✕ Close</button>
              </div>
            </div>
            <button
              type="button"
              aria-label={`Next ${unit}`}
              disabled={index === last}
              onClick={(e) => { e.stopPropagation(); setView(v => Math.min(v + 1, last)); }}
              style={navBtn(index === last)}
            >›</button>
          </div>
        );
      })()}
    </div>
  );
}

function SetRow({ s, href }) {
  // A sketch row shows the artist's stated production total (~n), the exact
  // number once the count is archive-verified (confidence high), and a
  // missing number says so plainly rather than faking one.
  const count = s.card_count_stated == null ? 'count not stated'
    : `${s.confidence === 'high' ? '' : '~'}${s.card_count_stated}`;
  const inner = (
    <div className="ap-setrow" style={{
      display: 'flex', alignItems: 'center', gap: 12, background: C.card,
      border: `1px solid ${C.lineSoft}`, borderLeft: `2px solid ${C.cyan}`,
      borderRadius: 2, padding: '12px 14px', transition: '.15s',
    }}>
      <span style={{ fontFamily: MONO, fontSize: 12, color: C.gold, minWidth: 38 }}>{s.year}</span>
      <span style={{ flex: 1, fontWeight: 600, fontSize: 15.5, letterSpacing: '.01em', color: C.ink }}>{s.set_name}</span>
      <span style={{ fontFamily: MONO, fontSize: 11, color: C.inkFaint, whiteSpace: 'nowrap' }}>
        {count}{!href && ' · no CK checklist yet'}
      </span>
    </div>
  );
  return href
    ? <a href={href} style={{ textDecoration: 'none' }}>{inner}</a>
    : inner;
}
